import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { UploadFile } from 'antd/lib';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import receivableRegistrationDocumentApi from '../../../apis/ReceivableRegistrationDocumentApi';
import useResponsiveLayout from '../../../components/LayoutComponent/UseResponsiveLayout/UseResponsiveLayout';
import { Council, ReceivableRegistration, ReceivableRegistrationDocument } from '../../../models/Entities';
import alertService from '../../../services/AlertService';

/**
 * Returns the summary component.
 * @returns the summary component.
 */
const SummaryComponent: React.FC<Props> = (props) => {
    const { onImport, onBack, receivableRegistrations, council, file } = props;

    /***HOOKS***/

    const [desktop] = useResponsiveLayout();
    const intl = useIntl();
    const [, setLoading] = useState<'loading'>();

    /***METHODS***/

    const save = async () => {
        try {
            setLoading('loading');
            let receivableRegistrationDocument: ReceivableRegistrationDocument = {
                councilId: council.id
            };
            receivableRegistrationDocument = await receivableRegistrationDocumentApi.create(receivableRegistrationDocument, file);

            onImport(receivableRegistrationDocument);
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /***VISUAL***/

    const columns: ColumnsType<ReceivableRegistration> = [
        {
            title: <FormattedMessage id="taxpayer.identifier" />,
            dataIndex: 'identifier',
            key: 'identifier',
            width: 150,
            fixed: 'left'
        },
        {
            title: <FormattedMessage id="taxpayer.firstName" />,
            dataIndex: 'firstName',
            key: 'firstName',
            width: 120,
            fixed: 'left'
        },
        {
            title: <FormattedMessage id="taxpayer.lastName" />,
            dataIndex: 'lastName',
            key: 'lastName',
            width: 120,
            fixed: 'left'
        },
        {
            title: <FormattedMessage id="address.tax" />,
            dataIndex: ['address', 'street'],
            key: 'street',
            width: 220
        },
        {
            title: <FormattedMessage id="address.postcode" />,
            dataIndex: ['address', 'postcode'],
            key: 'postcode',
            width: 150
        },
        {
            title: <FormattedMessage id="address.city" />,
            dataIndex: ['address', 'city'],
            key: 'city',
            width: 220
        },
        {
            title: <FormattedMessage id="address.province" />,
            dataIndex: ['address', 'province'],
            key: 'province',
            width: 220
        },
        {
            title: <FormattedMessage id="address.phone" />,
            dataIndex: ['address', 'phone'],
            key: 'phone',
            width: 220
        },
        {
            title: <FormattedMessage id="address.email" />,
            dataIndex: ['address', 'email'],
            key: 'email',
            width: 250
        },

        {
            title: <FormattedMessage id="receivable.name" />,
            dataIndex: 'tax',
            key: 'tax',
            align: 'center',
            width: 100
        },
        {
            title: <FormattedMessage id="receivable.taxObject" />,
            dataIndex: 'taxObject',
            key: 'taxObject',
            align: 'center',
            width: 200
        },
        {
            title: <FormattedMessage id="receivable.fiscalYear" />,
            dataIndex: 'fiscalYear',
            key: 'fiscalYear',
            align: 'center',
            width: 100
        },
        {
            title: <FormattedMessage id="receivable.fiscalPeriod" />,
            dataIndex: 'fiscalPeriod',
            key: 'fiscalPeriod',
            align: 'center',
            width: 100
        },
        {
            title: <FormattedMessage id="receivable.amount.principal" />,
            dataIndex: ['amount', 'principal'],
            key: 'principal',
            align: 'right',
            width: 100,
            render: (value: number) => (
                <FormattedNumber value={value} minimumFractionDigits={2} maximumFractionDigits={2} style={'currency' as any} currency="EUR" />
            )
        },
        {
            title: <FormattedMessage id="receivable.amount.surcharge" />,
            dataIndex: ['amount', 'surcharge'],
            key: 'surcharge',
            align: 'right',
            width: 100,
            render: (value: number) => (
                <FormattedNumber value={value} minimumFractionDigits={2} maximumFractionDigits={2} style={'currency' as any} currency="EUR" />
            )
        },
        {
            title: <FormattedMessage id="receivable.expired" />,
            dataIndex: 'expired',
            key: 'expired',
            align: 'right',
            width: 200
        },
        {
            title: <FormattedMessage id="receivable.reference" />,
            dataIndex: 'reference',
            key: 'reference',
            align: 'right',
            width: 150
        }
    ];

    return (
        <>
            <Table
                dataSource={receivableRegistrations}
                columns={columns}
                rowKey="id"
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                pagination={false}
                className="table"
                scroll={{ x: 2400 }}
            />
            <Row gutter={[28, 0]} className="buttons">
                <Col span={24}>
                    <Space>
                        <Button type="primary" size="large" icon={<SaveOutlined />} onClick={save}>
                            <FormattedMessage id="button.import" tagName="span" />
                        </Button>
                        <Button size="large" icon={<ArrowLeftOutlined />} onClick={onBack}>
                            {desktop && <FormattedMessage id="button.back" tagName="span" />}
                        </Button>
                    </Space>
                </Col>
            </Row>
        </>
    );
};
export default SummaryComponent;

interface Props {
    council: Council;
    receivableRegistrations: ReceivableRegistration[];
    file: UploadFile | undefined;
    onImport: (receivableRegistrationDocument: ReceivableRegistrationDocument) => void;
    onBack: () => void;
}
